import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { useAuth } from '../lib/auth-context';
import { formatBudget, formatDateNumeric } from '../lib/format';
import { nextChargeDate } from '../lib/next-charge';
import { COMPANY, HAS_TELEGRAM } from '../lib/company';
import { ApiError } from '../lib/api';
import { useVisualViewportSheetHeight } from '../lib/use-visual-viewport-sheet-height';
import { useSheetPanelViewportCap } from '../lib/use-sheet-panel-viewport-cap';
import { countryConfig } from '../country';
import {
  createInvoice,
  downloadInvoicePdf,
  lookupBuyer,
  sendInvoice,
  type IssuedInvoice,
  type InvoiceChannel,
} from '../lib/invoice-client';
import { ContactSupport } from './contact-support';

// UZ ИНН = 9 digits, KZ БИН/ИИН = 12 — the web only gates on length; the server
// does the real checksum. See `countryConfig.companyIdForm`.
const TIN_DIGITS = countryConfig.companyIdForm.digits;

/**
 * The sheet's pinned CTA footer — byte-for-byte the card sheet's treatment
 * (`card-save-modal.tsx`), and for the same reasons: it is the panel's last
 * flex row rather than anything `position: fixed`, so it rides inside the
 * dialog's own height cap instead of under the iOS keyboard, and the body above
 * it is a SIBLING, not a layer — nothing is hidden underneath, so no spacer and
 * no measured footer height are needed.
 */
const SHEET_FOOTER =
  'shrink-0 border-t border-edge bg-bone-surface px-6 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3';
/** The scrolling half. `flex-auto` + `min-h-0`, never `flex-1` — see the card
 *  sheet: `flex-basis:0` collapses a content-driven panel to its footer. */
const SHEET_BODY = 'min-h-0 flex-auto overflow-y-auto overscroll-contain px-6 pb-4 pt-5';
const TRUST_LINK = 'font-medium text-brand hover:underline';

/*
 * DELIVERY, RE-RANKED 2026-09-22.
 *
 * «Скачать PDF» was the filled brand button and the two channels were a pair of
 * small outlined ones beneath it. That ordering pointed buyers at the one action
 * that cannot work everywhere: inside an Android WebView a blob download is a
 * silent no-op (see `invoice-client.ts`), and on 2026-09-21 a buyer in the
 * Instagram browser pressed it 18 times in 29 seconds and left without the
 * document — while «На почту», one tap away, worked the first time.
 *
 * So e-mail leads (filled), Telegram follows, and the download comes THIRD —
 * but as a full outlined button, not a text link: it was demoted to a dotted
 * underline for a day and Igor sent it back (2026-09-22: «Скачать —
 * полноценная кнопка, а не только на почту»). The order carries the ranking;
 * the size does not have to. The server-ticket download (`invoice-client.ts`)
 * makes it work inside the WebViews that used to swallow it.
 */
const PRIMARY_ACTION =
  'inline-flex h-[46px] w-full items-center justify-center rounded-md bg-brand px-4 ' +
  'text-[14px] font-bold text-white transition-colors hover:bg-brand-mid';
const SECONDARY_ACTION =
  'inline-flex h-[46px] w-full items-center justify-center rounded-md border-[1.5px] ' +
  'border-brand bg-bone-surface px-4 text-[14px] font-bold text-brand transition-colors ' +
  'hover:bg-brand-dim';

/**
 * The «Оплата по счёту» flow, rendered INSIDE the dialog that /plans already
 * opens. It replaces the old `BankTransfer` body; the trigger button, the
 * `invoice_requested` event and the card grid on /plans are untouched, which is
 * the explicit constraint on this change.
 *
 * Three steps:
 *   1. ИНН + название организации → BOTH always on screen. The registry fills
 *      the name when it knows the ИНН and the buyer can correct it; when it does
 *      not, the field is simply empty and they type it. There is no «мы не нашли
 *      вашу организацию» any more (Igor, 2026-09-19) — our organisations table
 *      holds tender participants, not the state registry, so a miss is the
 *      ordinary case for a buyer who never bid, and announcing it as a failure
 *      put a scary sentence in front of a form that was working fine.
 *   2. The invoice: number, amount, and the copy-able payment purpose — the
 *      reason the feature exists. All four historical transfers were matched to
 *      their buyer by hand because the purpose said nothing identifying.
 *   3. Delivery: download, email, Telegram. The invoice also stays in the
 *      account, so closing the dialog loses nothing.
 */
export function InvoiceFlow({
  planKey,
  amountLabel,
  heading,
  onIssued,
  identify,
  beforeSubmit,
  afterIssued,
}: {
  planKey: string;
  /** Pre-issue amount from the plan card, e.g. «2 399 000 сум». */
  amountLabel: string;
  /**
   * The sheet's own title — in practice /plans' `<DialogTitle>` carrying
   * «Pro на год» / «Бизнес на год», i.e. the card sheet's title pattern.
   *
   * Passed in rather than built here because it has to BE the Radix dialog
   * title (Radix warns, and a screen reader announces nothing useful, without
   * one) and this component is also rendered bare in its own unit tests, where
   * a `DialogTitle` outside a `Dialog` root throws. Rendered in BOTH steps for
   * the same reason: unmounting it when the счёт is issued would drop the
   * dialog's accessible name mid-flow.
   *
   * Absent → no title, which is what the bare tests get.
   */
  heading?: import('react').ReactNode;
  /** Lets the host refresh a list of invoices; optional. */
  onIssued?: (invoice: IssuedInvoice) => void;
  /**
   * GUEST checkout: the email field, rendered ABOVE the ИНН on this same form —
   * in practice the node from `useGuestIdentify`.
   *
   * Igor, 2026-09-19: «можно без "далее" — просто поле почты появляется и всё,
   * ИНН вводит, если хочет название, дальше скачать и отправляет по почте либо
   * скачивает сразу». One form, one button. The shape before this put the
   * address on its own screen behind a «Продолжить», which is the same
   * two-decision shape he had rejected on the monthly card an hour earlier.
   *
   * ⚠️ Absent for a signed-in buyer, and every branch that reads it is guarded,
   * so the authed invoice renders exactly what it rendered before this prop
   * existed.
   */
  identify?: import('./guest-checkout-identify').GuestIdentify;
  /**
   * GUEST checkout: awaited on submit BEFORE the invoice is created — in
   * practice `useGuestIdentify`'s `ensure()`, which creates the account.
   *
   * Resolving `false` is not necessarily a failure: `code_required` and
   * `password_required` both return false and reveal their field inside
   * `identify`, leaving the ИНН and the name exactly as typed — they are this
   * component's own state and nothing unmounts. The buyer presses the same
   * button again.
   *
   * Absent → the submit path is the synchronous one it has always been.
   */
  beforeSubmit?: () => Promise<boolean>;
  /**
   * GUEST checkout: awaited once the счёт EXISTS — in practice
   * `useGuestIdentify`'s `commit()`, which turns the checkout token into a real
   * session.
   *
   * The other end of `beforeSubmit`. That one buys the account the invoice
   * hangs off (`invoices.user_id` is NOT NULL); this one gives the browser the
   * session, so the buyer can find the document in their account instead of
   * being a guest holding a PDF. The bank path has no charge to wait for — an
   * issued invoice IS the commitment — so this is its "the money has moved".
   *
   * Never rejects (see `GuestIdentify.commit`), so an issued invoice is never
   * un-issued on screen by a failure in the step after it.
   */
  afterIssued?: () => Promise<void>;
}) {
  const t = useT();
  /** Identification in flight (guest only) — keeps the one button honest while
   *  `/auth/start` is out. Permanently false without `beforeSubmit`. */
  const [identifying, setIdentifying] = useState(false);
  const [tin, setTin] = useState('');
  const [name, setName] = useState('');
  /** Set by a press that could not go through — what to light up. */
  const [showProblems, setShowProblems] = useState(false);
  const tinRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  /**
   * The sheet's SCROLLING body — the half beside the pinned «Выставить счёт»
   * footer, on BOTH steps (the form's body, and the issued-счёт body that
   * replaces it). The dialog panel is a flex column that does not scroll
   * itself, so this is the only element the keyboard hook can move.
   */
  const bodyRef = useRef<HTMLDivElement>(null);
  /*
   * KEYBOARD-SAFE HEIGHT — the same hook the card sheet, the Octobank sheet,
   * the guest identify sheet and the one-time sheet have all used since
   * 2026-09-20/22. This was the ONE payment sheet without it.
   *
   * Why it matters here and not only there: on iOS the software keyboard
   * shrinks the VISUAL viewport and leaves the layout viewport — and therefore
   * `vh`/`dvh` — exactly as it was, so a `max-h-[88vh]` bottom sheet keeps its
   * full height under the keyboard and its last flex row (the footer with
   * «Выставить счёт») sits behind the keys. The panel fits inside its own
   * max-height, so there is nothing to scroll to either. Measured on
   * 2026-09-25: a buyer in the Google app's in-app browser on iOS pressed
   * «Оплата по счёту» on the annual card at 08:14:46 Tashkent and left four
   * minutes later without an `invoices` row — 2 399 000 сум.
   *
   * The hook publishes `--sheet-max` / `--sheet-lift` on the document root;
   * the panel that CONSUMES them is /plans' `DialogContent` (see `bankSheet`
   * there), because the lift has to land on the `position: fixed` box.
   *
   * `open` is a literal `true`: this component is mounted only while the Radix
   * dialog is open (no `forceMount` anywhere), so mounting IS opening, and the
   * effect's cleanup removes both properties when the sheet goes away.
   */
  useVisualViewportSheetHeight(true, bodyRef);
  /*
   * …AND THE CAP THE PANEL CANNOT LOSE (2026-09-26).
   *
   * The lift is a `margin-bottom` on a `position: fixed; bottom: 0` box, so
   * `top = layoutHeight − lift − height`: the moment the height is NOT the
   * capped one, that goes negative and the panel leaves the screen through the
   * top. The cap therefore has to be in a unit the browser can actually
   * compute, and it has to be written where nothing can shadow it.
   *
   * Measured on a real phone (Chrome 101, Android 9, 393×744) on 2026-09-26, on
   * /plans → год → «Оплата по расчётному счёту»: the ИНН field's numeric
   * keyboard left both viewports at 534 and the 483px sheet still fitted —
   * which is why the 2026-09-25 check passed — while «Название организации»
   * brings text keys AND a suggestion strip, both viewports **448**, and the
   * panel stayed **483** tall with its top at **−237**: header, amount and ИНН
   * above the screen edge. «Модалка слишком вверх поднимается».
   *
   * That −237 is `448 − 202 − 483`, and BOTH of its terms were bugs:
   *   • 202 — the root hook's *assumed* 45% keyboard. It read the 534 → 448
   *     keyboard SWAP as an 86px step, too small to be a keyboard, and assumed
   *     one on top of the one that was already up and already handled by
   *     Chrome resizing the layout viewport. Fixed there, against the tallest
   *     `innerHeight` seen since the sheet opened.
   *   • 483 — an UNCAPPED panel. Chrome 101 predates `dvh` (Chrome 108), and
   *     every `--sheet-max` value in the chain carried one, so `max-height` was
   *     invalid at computed-value time and computed to `none`. Fixed in three
   *     places: the root hook writes px, this hook writes px, and the panel's
   *     Tailwind fallback is now `88vh` + a `supports-[height:1dvh]` twin.
   *
   * This hook is the one that engages on THAT phone, because the keyboard
   * arrives as a LAYOUT resize there and the visual-viewport test can never see
   * it: it caps the panel from `innerHeight` and pins the lift to `0px`. On iOS
   * it keeps doing what it did — cap from the visual viewport, then read the
   * panel's rect back and give the lift back if the top is still above the edge.
   * Same two properties, shadowed for this one element; with no keyboard
   * measurable either way it removes them again and the root hook (including
   * its assumed-keyboard path for the Instagram WebView, where nothing resizes
   * at all) is back in charge.
   */
  useSheetPanelViewportCap(bodyRef);
  /*
   * THE COMPANY THE ACCOUNT ALREADY CLAIMED.
   *
   * Igor, 2026-09-19: «в начале можно подставлять в поле их ИНН из регистрации,
   * но чтобы можно было менять, то же с названием.» A buyer who claimed a TIN at
   * registration has already told us who they are; making them retype it on the
   * one form that turns it into a payable document is asking twice.
   *
   * Prefill ONLY while the field is untouched, and only once — a re-render must
   * never overwrite what the buyer typed, and clearing the field on purpose must
   * not be undone. A guest has no account and so no `tin`; their identity comes
   * from the email field above instead.
   *
   * The ordinary lookup effect below then runs against it exactly as it would
   * for a hand-typed ИНН, filling «Название организации» from the registry — and
   * a different ИНН typed afterwards replaces the name the same way, with the
   * same sequence guard dropping a slow answer for a number that has moved on.
   */
  const { user } = useAuth();
  const accountTin = user?.tin ?? null;
  /*
   * Offer «В Telegram» only when it can work.
   *
   * Keyed on the USER OBJECT, not on `status`: a guest has no `user` at all, so
   * `!user` is exactly "no session yet" and keeps today's behaviour for them —
   * whereas `status` is also `'loading'` on the first paint of a signed-in
   * session, which would flash the line at someone who does have Telegram.
   */
  const showTelegramSend = !user || Boolean(user.telegramConnected);
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !accountTin || tin !== '') return;
    prefilled.current = true;
    setTin(accountTin);
  }, [accountTin, tin]);
  /** Registry name for the ИНН currently typed; null = looked up and missing. */
  const [registryName, setRegistryName] = useState<string | null | undefined>(undefined);
  const [invoice, setInvoice] = useState<IssuedInvoice | null>(null);
  const [copied, setCopied] = useState(false);
  const [sentOn, setSentOn] = useState<Partial<Record<InvoiceChannel, boolean>>>({});
  const [sendError, setSendError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState(false);
  const lookupSeq = useRef(0);

  const digits = tin.replace(/\D/g, '').slice(0, TIN_DIGITS);
  const tinComplete = digits.length === TIN_DIGITS;

  // Look the ИНН up as soon as it is nine digits. No debounce timer: the field
  // is fixed-length, so there is exactly one request per completed ИНН, and a
  // sequence guard drops a slow answer for a number the user has since edited.
  useEffect(() => {
    if (!tinComplete) {
      setRegistryName(undefined);
      return;
    }
    const seq = ++lookupSeq.current;
    let alive = true;
    void lookupBuyer(digits)
      .then((r) => {
        if (!alive || seq !== lookupSeq.current) return;
        setRegistryName(r.name);
        /*
         * PREFILL, don't announce. The name field is always on screen now, so a
         * registry hit fills it instead of replacing it with a read-only line —
         * the buyer still sees the official name, and can fix it when the
         * registry spelling is not what their bank expects.
         *
         * A MISS leaves the field alone rather than clearing it: in the ordinary
         * flow it is empty anyway, and in the one flow where it is not (a name
         * typed before the ИНН was finished) wiping it would delete the buyer's
         * own typing to tell them nothing.
         */
        if (r.name) setName(r.name);
      })
      .catch(() => {
        // A failed lookup must not block the invoice — the buyer can type the
        // name. Falling back is the whole reason `buyerName` is accepted.
        if (alive && seq === lookupSeq.current) setRegistryName(null);
      });
    return () => {
      alive = false;
    };
  }, [digits, tinComplete]);

  const issue = useMutation({
    mutationFn: () =>
      createInvoice({
        planKey,
        ...(tinComplete ? { buyerTin: digits } : {}),
        ...(name.trim() ? { buyerName: name.trim() } : {}),
      }),
    onSuccess: async (created) => {
      setInvoice(created);
      onIssued?.(created);
      // …and only now, with the document on screen, does a guest become a
      // member. Last, so nothing about the счёт waits on it.
      await afterIssued?.();
    },
  });

  const send = useMutation({
    mutationFn: (channel: InvoiceChannel) => sendInvoice(invoice!.id, channel),
    onSuccess: (_r, channel) => {
      setSentOn((s) => ({ ...s, [channel]: true }));
      setSendError(null);
    },
    onError: (e: unknown) => {
      setSendError(e instanceof ApiError ? (e.code ?? e.message) : 'send_failed');
    },
  });

  /*
   * The buyer's name is ALWAYS required, whatever the registry said.
   *
   * ⚠️ The old predicate treated a registry MISS (`registryName === null`) as
   * "resolved", so «Выставить счёт» stayed enabled with an empty name and the
   * server fell back to naming the payer «ИНН 309048804» — THREE of the first
   * eight invoices we ever issued read that way, and a bank clerk has no reason
   * to accept one. With the field always visible and prefilled from the
   * registry, requiring it costs a recognised buyer nothing (it is already
   * filled) and costs an unrecognised one the one thing the document cannot do
   * without. The ИНН itself stays optional — a private buyer with no company
   * can still be invoiced by name.
   */
  /*
   * What is wrong RIGHT NOW, per field. Computed every render so a press can
   * light up all of them at once and a keystroke can put one out again.
   *
   * ⚠️ An EMPTY ИНН is fine — a private buyer with no company is invoiced by
   * name, which is what «can be submitted with a typed name and no ИНН» has
   * always allowed. A PARTIAL one is not: `tinComplete` is false, so the digits
   * are silently dropped from the request and the buyer gets a document with no
   * ИНН on it and no idea why. That is the case this marks.
   */
  const tinProblem = digits.length > 0 && !tinComplete;
  const nameProblem = name.trim().length === 0;
  const canIssue = !tinProblem && !nameProblem;

  /**
   * Send the счёт down one channel.
   *
   * NEVER `disabled` (Igor, 2026-09-20 — the checkout rule, and it applies here
   * for the same reason): a control greyed out mid-flight looks broken, and the
   * buyer who taps it again is asking for the document a second time, which is
   * a perfectly sensible thing to want. A request ALREADY in the air is the one
   * thing dropped, so a double-tap cannot produce two e-mails.
   *
   * The previous error is cleared on the way in, so the region under these
   * buttons carries one message — the current one — and never a stale refusal
   * next to a send that has just succeeded.
   */
  function deliver(channel: InvoiceChannel) {
    if (send.isPending) return;
    setSendError(null);
    send.mutate(channel);
  }

  async function copyPurpose() {
    if (!invoice) return;
    try {
      await navigator.clipboard.writeText(invoice.purpose);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is permission-gated and absent in some in-app browsers. The
      // purpose is rendered selectable, so the buyer can still copy it by hand.
      setCopied(false);
    }
  }

  // ── step 2 / 3 ─────────────────────────────────────────────────────────
  if (invoice) {
    return (
      /* The scroller on THIS step too. It has no fields of its own, so nothing
         here opens a keyboard — but the step-1 body unmounts when the счёт is
         issued, and without this the hook would keep a ref to a detached node
         for the rest of the sheet's life (the effect does not re-run: `open`
         and the ref object are both stable across the step change). */
      <div ref={bodyRef} className={`${SHEET_BODY} flex flex-col gap-4 pb-6`}>
        {heading}
        <div>
          <div className="text-[15px] font-bold text-ink">
            {t('invoiceFlow.readyTitle', { number: invoice.number })}
          </div>
          <div className="mt-0.5 text-[20px] font-bold text-ink">
            {formatBudget(String(invoice.amountUzs))} {t('plans.som')}
          </div>
          <div className="mt-0.5 text-[12.5px] text-ink-muted">{invoice.buyerName}</div>
        </div>

        <div className="rounded-md border border-edge bg-bone p-3">
          <div className="text-[11.5px] font-bold uppercase tracking-wide text-ink-muted">
            {t('invoiceFlow.purposeLabel')}
          </div>
          <p className="mt-1 select-all break-words text-[13.5px] font-semibold leading-snug text-ink">
            {invoice.purpose}
          </p>
          <button
            type="button"
            onClick={() => void copyPurpose()}
            className="mt-2 inline-flex h-9 items-center rounded-md border-[1.5px] border-brand px-3 text-[13px] font-bold text-brand transition-colors hover:bg-brand-dim"
          >
            {copied ? t('invoiceFlow.copied') : t('invoiceFlow.copy')}
          </button>
          <p className="mt-2 text-[11.5px] leading-snug text-ink-muted">
            {t('invoiceFlow.purposeWarning')}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {/* Two explicit buttons rather than a `.map()` with computed keys:
              `i18n/keys-exist.test.ts` only sees LITERAL `t('…')` calls, so a
              templated key is a key nobody guards. */}
          <button type="button" onClick={() => deliver('email')} className={PRIMARY_ACTION}>
            {sentOn.email ? t('invoiceFlow.emailSent') : t('invoiceFlow.sendEmail')}
          </button>
          {/*
           * «В Telegram» is only offered to someone it can actually reach.
           * The server answers 400 `no_telegram` for an account with no
           * linked chat (invoice.service.ts), and three of those came from
           * two buyers on 2026-09-20 alone: the button looked like a delivery
           * channel, took a tap, and answered with a refusal. A control that
           * cannot succeed is not a choice, it is a trap — so it is replaced
           * by the one thing that WOULD make it work.
           *
           * GUESTS keep the button. There is no session yet, so there is
           * nothing to ask about: the account is created by this very submit,
           * and the invoice reaches them by e-mail and by download either way.
           */}
          {showTelegramSend && (
            <button type="button" onClick={() => deliver('telegram')} className={SECONDARY_ACTION}>
              {sentOn.telegram ? t('invoiceFlow.telegramSent') : t('invoiceFlow.sendTelegram')}
            </button>
          )}
          {!showTelegramSend && (
            <p className="text-[12px] leading-snug text-ink-muted">
              <Link
                to="/app"
                className="font-medium text-brand underline decoration-dotted underline-offset-2"
              >
                {t('invoiceFlow.connectTelegram')}
              </Link>
            </p>
          )}
          {sendError && (
            <p className="text-[12.5px] text-status-closing-text" role="alert">
              {sendError === 'no_telegram'
                ? t('invoiceFlow.errNoTelegram')
                : sendError === 'no_email'
                  ? t('invoiceFlow.errNoEmail')
                  : t('invoiceFlow.errSend')}
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              // A rejected fetch used to vanish into `void`: the button looked
              // dead and the buyer clicked it eight times. Say so instead, and
              // point at the two delivery channels that still work.
              setDownloadError(false);
              downloadInvoicePdf(invoice).catch(() => setDownloadError(true));
            }}
            className={SECONDARY_ACTION}
          >
            {t('invoiceFlow.download')}
          </button>
          {downloadError && (
            <p className="text-center text-[12.5px] text-status-closing-text" role="alert">
              {t('invoiceFlow.errDownload')}
            </p>
          )}
        </div>

        <p className="text-[12px] leading-[1.5] text-ink-muted">
          {t('invoiceFlow.activationNote')}{' '}
          <ContactSupport
            defaultCategory="subscription"
            className="font-medium text-brand underline decoration-dotted underline-offset-2"
          >
            {t('invoiceFlow.needFaster')}
          </ContactSupport>
        </p>
        <p className="text-[12px] leading-[1.5] text-ink-muted">{t('invoiceFlow.savedNote')}</p>
      </div>
    );
  }

  /*
   * WHEN THE ACCESS ENDS if this счёт is paid today.
   *
   * The one fact a yearly bank transfer never stated. «Доступ 12 месяцев со дня
   * активации» is the rule, but the rule alone leaves the buyer computing a
   * date from an activation they have not reached yet, so the concrete
   * "paid today → until DD.MM.YYYY" is spelled out beside it.
   *
   * `nextChargeDate('annual')` is the SERVER's own period arithmetic (plain
   * `setFullYear`, not luxon — the two disagree at month ends), so the line
   * cannot promise a different day than the subscription will get. This sheet
   * is reached from the annual card only, which is why the period is a
   * constant here.
   */
  const accessUntil = formatDateNumeric(nextChargeDate('annual').toISOString());

  // ── step 1 ─────────────────────────────────────────────────────────────
  return (
    <form
      /* The FORM is the flex column, so the button in the pinned footer stays a
         plain `type="submit"` inside it — no `form=` id round trip, and Enter in
         any field behaves exactly as it did. */
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (issue.isPending || identifying) return;
        /*
         * PRESS AND HIGHLIGHT, never a dead button (Igor, 2026-09-19). Everything
         * missing is marked on THIS press — the address, the ИНН and the name
         * together — and the caret goes to the first of them in reading order,
         * which is the email when there is one.
         *
         * `precheck` is synchronous and reaches no API, so the email is lit at
         * the same moment as the two fields below it instead of one error per
         * press. It also emits the one event it has always emitted for a
         * malformed address (`login_failed{client:true}`) and nothing new for
         * the ИНН or the name: an unfinished form is not a rejected attempt.
         */
        const emailOk = identify?.precheck() ?? true;
        setShowProblems(true);
        if (!canIssue) {
          if (emailOk) (tinProblem ? tinRef : nameRef).current?.focus();
          return;
        }
        if (!emailOk) return;
        /*
         * GUEST: the account is created by THIS press, before the счёт — an
         * invoice needs a buyer (`invoices.user_id` is NOT NULL), and there is
         * no second sheet left to ask on. A `false` means the address needed a
         * code or a password; that field is now inside `identify` and the ИНН
         * and name above it are untouched, so the next press continues.
         *
         * No guest → the original synchronous call, unchanged.
         */
        if (!beforeSubmit) {
          issue.mutate();
          return;
        }
        setIdentifying(true);
        void beforeSubmit()
          .then((ok) => {
            if (ok) issue.mutate();
          })
          .finally(() => setIdentifying(false));
      }}
    >
      {/* SCROLLING BODY — everything but the CTA. `bodyRef` hands it to
          `useVisualViewportSheetHeight`, which scrolls a focused field into it
          rather than `scrollIntoView`-ing the page out from under a fixed
          sheet. Every input on this step (the guest's e-mail, the ИНН, the
          name) lives here, so this is the element the keyboard has to move. */}
      <div ref={bodyRef} className={`${SHEET_BODY} flex flex-col gap-4`}>
        {/* WHICH PLAN, unmistakably (Igor, 2026-09-20). The sheet used to open
            on «Оплата по расчётному счёту» and a 13px «Сумма к оплате» line, so
            a buyer who had tapped through from a card grid could not tell Pro
            from Бизнес on the screen that turns it into a payable document.
            Same pattern as the card sheet: plan name + period, then the amount
            big and bold. */}
        <div>
          {heading}
          <div className="mt-1 text-[28px] font-extrabold leading-none tracking-[-0.4px] text-ink">
            {amountLabel}
          </div>
          {accessUntil && (
            <p className="mt-2 text-[12.5px] leading-snug text-ink-soft">
              {t('invoiceFlow.accessPeriod', { date: accessUntil })}
            </p>
          )}
        </div>

        {/* GUEST: who is buying, above what they are buying it as. Null for a
            signed-in buyer, so the ИНН label below is the first thing on the form
            exactly as before. */}
        {identify?.node}

        <label className="flex flex-col gap-1.5">
          <span className="text-[12.5px] font-semibold text-ink-soft">
            {t('invoiceFlow.tinLabel')}
          </span>
          <input
            ref={tinRef}
            value={digits}
            onChange={(e) => setTin(e.target.value)}
            inputMode="numeric"
            autoComplete="off"
            maxLength={TIN_DIGITS}
            placeholder="301234567"
            aria-invalid={showProblems && tinProblem}
            className={`h-[46px] rounded-md border-[1.5px] bg-bone-surface px-3 text-[16px] tabular-nums text-ink outline-none md:text-[15px] ${
              showProblems && tinProblem
                ? 'border-status-closing-text focus:border-status-closing-text'
                : 'border-edge focus:border-brand'
            }`}
          />
          {showProblems && tinProblem && (
            <span className="text-[12px] font-medium text-status-closing-text" role="alert">
              {t('invoiceFlow.tinIncomplete')}
            </span>
          )}
          {/* The lookup is still worth narrating while it is in flight — it is the
            reason the name field is about to fill itself. A MISS says nothing:
            an empty field the buyer can type in needs no explanation, and the
            sentence it used to print («Не нашли этот ИНН…») read as a refusal on
            a form that was working. */}
          {tinComplete && registryName === undefined && (
            <span className="text-[12px] text-ink-muted">{t('invoiceFlow.tinLooking')}</span>
          )}
        </label>

        {/* ALWAYS on screen, both of them (Igor, 2026-09-19). Hiding the name
          behind a registry miss made the form change shape while it was being
          filled in, and made «выставить счёт» mean two different things
          depending on a lookup the buyer never asked for. A recognised ИНН
          prefills this field instead of replacing it — same information, still
          editable, and required either way. */}
        <label className="flex flex-col gap-1.5">
          <span className="text-[12.5px] font-semibold text-ink-soft">
            {t('invoiceFlow.nameLabel')}
          </span>
          <input
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={300}
            placeholder={t('invoiceFlow.namePlaceholder')}
            aria-invalid={showProblems && nameProblem}
            className={`h-[46px] rounded-md border-[1.5px] bg-bone-surface px-3 text-[16px] text-ink outline-none md:text-[15px] ${
              showProblems && nameProblem
                ? 'border-status-closing-text focus:border-status-closing-text'
                : 'border-edge focus:border-brand'
            }`}
          />
          {showProblems && nameProblem && (
            <span className="text-[12px] font-medium text-status-closing-text" role="alert">
              {t('invoiceFlow.nameRequired')}
            </span>
          )}
        </label>

        {/* ⚠️ NO «наши реквизиты» card here any more (Igor, 2026-09-20). The
            bank details belong on the документ — they are printed on the PDF and
            repeated in the email that carries it — and eleven rows of МФО /
            ОКЭД / Р/С above the button are eleven rows the buyer has to scroll
            past to reach a form with two fields in it. Nothing reads them at
            this moment; the счёт does not exist yet. `/requisites` still
            publishes them for the e-commerce-law disclosure. */}

        <p className="text-[12px] leading-[1.5] text-ink-muted">
          {t('invoiceFlow.activationNote')}
        </p>
      </div>

      {/* PINNED CTA FOOTER — the card sheet's, exactly (see SHEET_FOOTER). */}
      <div className={SHEET_FOOTER}>
        <button
          type="submit"
          /* ⚠️ NEVER disabled for a missing field — the press is what explains
             what is missing. In flight only. */
          disabled={issue.isPending || identifying}
          className="inline-flex h-[46px] w-full items-center justify-center rounded-md bg-brand px-4 text-[14px] font-bold text-white transition-colors hover:bg-brand-mid disabled:opacity-60"
        >
          {issue.isPending || identifying ? t('invoiceFlow.issuing') : t('invoiceFlow.issue')}
        </button>

        {/* The refusal rides WITH the button, like the card sheet's, so it can
            never be scrolled away from the control it refers to. */}
        {issue.isError && (
          <p
            className="mt-2 rounded-md border border-status-closing-text/30 bg-status-closing-bg px-3 py-2 text-center text-[12.5px] font-medium leading-snug text-status-closing-text"
            role="alert"
          >
            {t('invoiceFlow.errIssue')}
          </p>
        )}

        {/* The subscription sheet's trust row, and only that: оферта,
            конфиденциальность, поддержка. The card sheet also carries a lock
            line and a 7-day line; neither is true of a bank transfer, so
            neither is copied. */}
        <div className="mt-2.5 text-center text-[12px] leading-snug text-ink-soft">
          <Link to="/offer" target="_blank" rel="noopener noreferrer" className={TRUST_LINK}>
            {t('billing.card.offerLink')}
          </Link>
          {' · '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer" className={TRUST_LINK}>
            {t('billing.card.privacyLink')}
          </Link>
          {HAS_TELEGRAM && (
            <>
              {' · '}
              <a
                href={COMPANY.telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={TRUST_LINK}
              >
                {t('billing.card.supportTg')}
              </a>
            </>
          )}
        </div>
      </div>
    </form>
  );
}
